import { createContext, useContext, useEffect, useState } from "react";
import { api } from "./api";

const AuthContext = createContext(null);

export const ROLE_PREVIEW_KEY = "dojova_role_preview";
export const ROLE_PREVIEW_PRESETS = {
  coach: { label: "Coach", roles: ["coach"] },
  reception: { label: "Réception", roles: ["reception"] },
  comptable: { label: "Comptable", roles: ["comptable"] },
  marketing: { label: "Marketing", roles: ["marketing"] },
  reception_prospects: { label: "Réception — Prospects et cours d'essai", roles: ["reception_prospects"] },
};

function readRolePreview() {
  try { const v = sessionStorage.getItem(ROLE_PREVIEW_KEY); return v ? JSON.parse(v) : null; } catch { return null; }
}

// Aperçu d'un rôle : l'admin (propriétaire) voit la navigation EXACTE d'un rôle sans changer de compte (filtrage côté client seulement).
export function applyRolePreview(user, preview) {
  if (!user || !preview || !(user.is_owner || user.is_admin)) return user;
  const preset = ROLE_PREVIEW_PRESETS[preview.role] || { label: preview.label || preview.role, roles: [preview.role] };
  const roles = preview.role === "custom" ? ["custom"] : preset.roles;
  return { ...user, is_admin: false, is_owner: false, role: preview.role, roles, allowed_groups: preview.allowed_groups || [],
    role_preview: { role: preview.role, label: preview.label || preset.label }, real_user: user };
}

export function AuthProvider({ children }) {
  const [realUser, setUser] = useState(() => {
    const u = localStorage.getItem("map_user");
    return u ? JSON.parse(u) : null;
  });
  const [rolePreview, setRolePreviewState] = useState(readRolePreview);
  const setRolePreview = (p) => {
    if (p) sessionStorage.setItem(ROLE_PREVIEW_KEY, JSON.stringify(p)); else sessionStorage.removeItem(ROLE_PREVIEW_KEY);
    setRolePreviewState(p);
  };
  const user = applyRolePreview(realUser, rolePreview);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Si retour de Google Auth (session_id dans le fragment), on laisse AuthCallback gérer.
    if (window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("map_token");
    if (!token) { setLoading(false); return; }
    api.get("/auth/me")
      .then((r) => {
        setUser(r.data);
        localStorage.setItem("map_user", JSON.stringify(r.data));
      })
      .catch(() => {
        localStorage.removeItem("map_token");
        localStorage.removeItem("map_user");
        setUser(null);
      })
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.twofa_required) {
      return { twofa_required: true, challenge_id: data.challenge_id, email_masked: data.email_masked,
        delivery: data.delivery || "email", delivery_error: data.delivery_error || "" };
    }
    localStorage.setItem("map_token", data.access_token);
    localStorage.setItem("map_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const verify2fa = async (challenge_id, code) => {
    const { data } = await api.post("/auth/login/verify-2fa", { challenge_id, code });
    localStorage.setItem("map_token", data.access_token);
    localStorage.setItem("map_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const register = async (email, password, full_name) => {
    const { data } = await api.post("/auth/register", { email, password, full_name });
    localStorage.setItem("map_token", data.access_token);
    localStorage.setItem("map_user", JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("map_token");
    localStorage.removeItem("map_user");
    sessionStorage.removeItem(ROLE_PREVIEW_KEY);
    setRolePreviewState(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, realUser, rolePreview, setRolePreview, loading, login, verify2fa, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
